import React, { useCallback, useEffect, useState } from 'react';
import {
  Cpu, RefreshCw, AlertTriangle, CheckCircle, Layers, Activity,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LineChart, Line,
} from 'recharts';
import { adminService } from '../services/adminService';
import { formatCompactNumber, formatNumber, formatPercent, formatDateTime, safeNumber } from '../utils/format';
import './AdminPage.css';
import './Tasks.css';

const SCENARIO_OPTIONS = [
  { value: '', label: 'Live providers (no override)' },
  { value: 'all_pending', label: 'All providers pending' },
  { value: 'no_inventory', label: 'No inventory available' },
  { value: 'force_adgem', label: 'Force AdGem only' },
  { value: 'force_theoremreach', label: 'Force TheoremReach only' },
  { value: 'force_rapidoreach', label: 'Force RapidoReach only' },
  { value: 'force_bitlabs', label: 'Force BitLabs only' },
  { value: 'force_pollfish', label: 'Force Pollfish only' },
  { value: 'force_security_check', label: 'Force security check' },
];

const Tasks: React.FC = () => {
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [adSummary, setAdSummary] = useState<any | null>(null);
  const [adSeries, setAdSeries] = useState<any[]>([]);
  const [rewardedAnalytics, setRewardedAnalytics] = useState<any | null>(null);
  const [callbackLogs, setCallbackLogs] = useState<any[]>([]);
  const [callbackCounts, setCallbackCounts] = useState<Record<string, number>>({});
  const [callbackNote, setCallbackNote] = useState<string | null>(null);
  const [scenario, setScenario] = useState<string>('');
  const [scenarioStatus, setScenarioStatus] = useState<string>('');

  const load = useCallback(async () => {
    setRefreshing(true);
    const [sumRes, seriesRes, rewardedRes, logsRes, scenarioRes] = await Promise.allSettled([
      adminService.getAdEventsSummary(days),
      adminService.getAdEventsTimeseries(days),
      adminService.getRewardedAdsAnalytics(days),
      adminService.getAisCallbackLogs({ days, limit: 100 }),
      adminService.getTaskTestScenario().catch(() => null),
    ]);
    if (sumRes.status === 'fulfilled') setAdSummary(sumRes.value);
    if (seriesRes.status === 'fulfilled') setAdSeries(seriesRes.value?.series || []);
    if (rewardedRes.status === 'fulfilled') setRewardedAnalytics(rewardedRes.value);
    if (logsRes.status === 'fulfilled') {
      setCallbackLogs(logsRes.value?.items || []);
      setCallbackCounts(logsRes.value?.counts_by_provider || {});
      setCallbackNote(logsRes.value?.note || null);
    }
    if (scenarioRes.status === 'fulfilled' && scenarioRes.value) {
      setScenario(scenarioRes.value?.scenario || '');
    }
    setLoading(false);
    setRefreshing(false);
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const handleApplyScenario = async () => {
    setScenarioStatus('Applying…');
    try {
      if (!scenario) {
        await adminService.clearTaskTestScenario();
      } else {
        await adminService.setTaskTestScenario(scenario, 3600);
      }
      setScenarioStatus('Saved.');
      setTimeout(() => setScenarioStatus(''), 2500);
    } catch (e: any) {
      setScenarioStatus(`Failed: ${e?.message || 'unknown error'}`);
    }
  };

  const eventTotal = adSummary
    ? Object.values(adSummary.counts_by_type || {}).reduce((a: number, n: any) => a + safeNumber(n), 0)
    : 0;
  const completionRate = adSummary ? (adSummary.rewarded_completion_rate || 0) * 100 : 0;
  const avgEcpm = adSummary?.avg_ecpm ?? 0;

  const eventTypeRows = adSummary
    ? Object.entries(adSummary.counts_by_type || {})
      .map(([type, count]) => ({ type, count: safeNumber(count) }))
      .sort((a, b) => b.count - a.count)
    : [];

  const providerRows = Object.entries(callbackCounts)
    .map(([provider, count]) => ({ provider, count }))
    .sort((a, b) => (b.count as number) - (a.count as number));

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1><Cpu size={20} style={{ marginRight: 8 }} />Tasks / Offerwall</h1>
          <p className="page-subtitle">Provider conversions, callback ingestion, and test scenarios</p>
        </div>
        <div className="header-actions">
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value={1}>Last 24h</option>
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
          </select>
          <button className="btn" onClick={load} disabled={refreshing}>
            <RefreshCw className={refreshing ? 'spin' : ''} size={14} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="kpi-grid">
        <div className="kpi-card">
          <span className="kpi-label">Total ad events</span>
          <span className="kpi-value">{formatCompactNumber(eventTotal)}</span>
          <span className="kpi-detail">Last {days}d</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Rewarded completion</span>
          <span className="kpi-value">{formatPercent(completionRate, 1)}</span>
          <span className="kpi-detail">starts → completes</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Average eCPM</span>
          <span className="kpi-value">${Number(avgEcpm).toFixed(2)}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Callback events</span>
          <span className="kpi-value">{formatCompactNumber(callbackLogs.length)}</span>
          <span className="kpi-detail">{Object.keys(callbackCounts).length} providers</span>
        </div>
      </div>

      <div className="charts-row-2">
        <div className="panel">
          <div className="panel-header">
            <h3><Activity size={16} /> Event Volume by Day</h3>
          </div>
          <div className="panel-body">
            {loading ? <div className="skeleton" style={{ height: 240 }} /> : adSeries.length === 0 ? (
              <div className="empty">No ad events recorded yet</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={adSeries.map((d) => ({
                  date: d.date,
                  total: Object.values(d.counts_by_type || {}).reduce((a: number, n: any) => a + safeNumber(n), 0),
                  ecpm: d.avg_ecpm,
                }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="total" name="Total events" stroke="#410C6D" strokeWidth={2} />
                  <Line type="monotone" dataKey="ecpm" name="Avg eCPM ($)" stroke="#22c55e" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
        <div className="panel">
          <div className="panel-header">
            <h3><Layers size={16} /> Events by Type</h3>
          </div>
          <div className="panel-body">
            {loading ? <div className="skeleton" style={{ height: 240 }} /> : eventTypeRows.length === 0 ? (
              <div className="empty">No event types reported</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={eventTypeRows} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis dataKey="type" type="category" width={140} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" name="Events" fill="#410C6D" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="panel-header">
          <h3><Cpu size={16} /> Provider Callbacks ({days}d)</h3>
          {callbackNote && <span className="pill warning">{callbackNote}</span>}
        </div>
        <div className="panel-body tight">
          {providerRows.length === 0 ? (
            <div className="empty">No provider callbacks recorded yet</div>
          ) : (
            <table className="data-table">
              <thead>
                <tr><th>Provider</th><th>Calls</th></tr>
              </thead>
              <tbody>
                {providerRows.map((r) => (
                  <tr key={r.provider as string}>
                    <td>{r.provider as string}</td>
                    <td>{formatNumber(r.count as number)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="panel">
        <div className="panel-header">
          <h3><Activity size={16} /> Recent callback events</h3>
        </div>
        <div className="panel-body tight">
          {callbackLogs.length === 0 ? (
            <div className="empty">No callbacks ingested yet</div>
          ) : (
            <div style={{ maxHeight: 380, overflowY: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>When</th><th>Provider</th><th>User</th><th>Amount</th><th>Status</th><th>IP</th>
                  </tr>
                </thead>
                <tbody>
                  {callbackLogs.map((log) => (
                    <tr key={log.id}>
                      <td>{formatDateTime(log.created_at)}</td>
                      <td>{log.provider}</td>
                      <td>{log.user_id ?? '—'}</td>
                      <td>{log.amount != null ? formatNumber(log.amount) : '—'}</td>
                      <td>
                        <span className={`pill ${log.status === 'ok' ? 'healthy' : log.status ? 'warning' : ''}`}>
                          {log.status || 'received'}
                        </span>
                      </td>
                      <td>{log.ip_address || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="panel">
        <div className="panel-header">
          <h3><AlertTriangle size={16} /> Test Scenario Override</h3>
        </div>
        <div className="panel-body">
          <p style={{ marginTop: 0, color: '#64748b', fontSize: '0.85rem' }}>
            Use these overrides on staging to deterministically test offerwall paths. Cleared automatically after 1 hour.
          </p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <select value={scenario} onChange={(e) => setScenario(e.target.value)}>
              {SCENARIO_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <button className="btn" onClick={handleApplyScenario}>
              <CheckCircle size={14} /> Apply
            </button>
            {scenarioStatus && <span style={{ fontSize: '0.85rem', color: '#475569' }}>{scenarioStatus}</span>}
          </div>
        </div>
      </div>

      {rewardedAnalytics && (
        <div className="panel">
          <div className="panel-header">
            <h3><Activity size={16} /> Rewarded Ads Analytics</h3>
          </div>
          <div className="panel-body">
            <pre style={{ background: '#f8fafc', padding: 12, borderRadius: 8, overflow: 'auto', fontSize: 12 }}>
              {JSON.stringify(rewardedAnalytics, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tasks;
